import React, { useEffect, useState } from "react";
import { Table } from "react-bootstrap";
import { API_BASE_URL } from "../../utils/constants";
import { readErrorMessage } from "../../utils/apiError";

const REMOVE = "__remove";

// The bank's draft has blanks («Customer_Name»…): pick what goes in each one, then the system updates the Word file
const MatchBlanks = ({ upload, doc, onMatched }) => {
  const [choice, setChoice] = useState(upload.suggestions || {});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // Single details, then the details of each item of a list (for blanks inside a "repeat for every…" part)
  const options = [
    ...doc.variables.filter((v) => v.kind === "text"),
    ...doc.variables
      .filter((v) => v.kind === "list")
      .flatMap((list) =>
        list.fields
          .filter((f) => f.kind === "text")
          .map((f) => ({ ...f, group: `For every item of "${list.label}" (inside its repeat part)` })),
      ),
  ].filter((v, i, all) => all.findIndex((o) => o.key === v.key && o.group === v.group) === i);
  const groups = [...new Set(options.map((v) => v.group))];
  const chosen = upload.blanks.filter((b) => choice[b]);

  useEffect(() => setChoice(upload.suggestions || {}), [upload]);

  const apply = async () => {
    setSaving(true);
    setError("");
    try {
      const mapping = Object.fromEntries(chosen.map((b) => [b, choice[b] === REMOVE ? "" : choice[b]]));
      const res = await fetch(`${API_BASE_URL}/api/doc-templates/uploads/${upload.id}/blanks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mapping }),
      });
      if (!res.ok) throw new Error(await readErrorMessage(res));
      onMatched(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-2 mb-2 bg-white border rounded">
      <div className="fw-semibold mb-1">Match the blanks</div>
      <div className="text-muted mb-2">
        The bank's Word file has {upload.blanks.length} blank{upload.blanks.length === 1 ? "" : "s"}. Choose what the system
        should print in each one; every case prints its own details there. Our best guesses are filled in already —
        please check them.
      </div>
      <Table size="sm" bordered className="mb-2 align-middle" style={{ fontSize: "11px" }}>
        <thead className="table-light">
          <tr>
            <th style={{ width: "35%" }}>Blank in the bank's file</th>
            <th>What to print here (taken from each case)</th>
          </tr>
        </thead>
        <tbody>
          {upload.blanks.map((blank) => (
            <tr key={blank}>
              <td>«{blank}»</td>
              <td>
                <select
                  className={`form-select form-select-sm ${choice[blank] ? "" : "border-warning"}`}
                  style={{ fontSize: "11px" }}
                  value={choice[blank] || ""}
                  onChange={(e) => setChoice((c) => ({ ...c, [blank]: e.target.value }))}
                >
                  <option value="">— choose —</option>
                  <option value={REMOVE}>Nothing (remove this blank)</option>
                  {groups.map((group) => (
                    <optgroup key={group} label={group}>
                      {options
                        .filter((v) => v.group === group)
                        .map((v) => (
                          <option key={`${group}.${v.key}`} value={v.key}>
                            {v.label}
                          </option>
                        ))}
                    </optgroup>
                  ))}
                </select>
                {choice[blank] && choice[blank] !== REMOVE && (
                  <div className="text-muted mt-1">
                    Each case prints its own. Example: {options.find((v) => v.key === choice[blank])?.example || "—"}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {error && <div className="text-danger mb-1">{error}</div>}
      <div className="d-flex align-items-center gap-2">
        <button type="button" className="btn btn-sm btn-primary" disabled={saving || !chosen.length} onClick={apply}>
          {saving ? "Applying…" : `Apply to the Word file (${chosen.length} of ${upload.blanks.length})`}
        </button>
        {chosen.length < upload.blanks.length && (
          <span className="text-muted">Blanks left on "— choose —" stay in the file and still need a choice.</span>
        )}
      </div>
    </div>
  );
};

export default MatchBlanks;
